import { logger } from '@adornis/base/logging.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { OSCE } from '@ils/osces/db/osce.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { checkForAnswersInSlots } from '@ils/slots/api/slot.js';
import { Slot } from '@ils/slots/db/slot.js';
import { DateTime } from 'luxon';
import { ExamDay, type OsceTesterTuple } from './examdays.js';
import { ILSUser } from './ils-user.js';

export const SLOT_PAUSE_LENGTH = 1;

/**
 * Create an ExamSlot on the given ExamDay.
 */
function createSlot(
  startTime: DateTime,
  endTime: DateTime,
  candidate: string,
  osceTuple: OsceTesterTuple,
  osce: OSCE,
  round: number,
) {
  const testerID = osceTuple.testerIDs?.[round - 1];
  if (!testerID && osce.taskType !== 'text') {
    logger.error({ osceTuple, testerID });
    throw new Error(
      `Couldnt schedule exam day, there was missing an assigned tester for osce ${osceTuple.osceID} in round ${round}`,
    );
  }
  const examSlot = new Slot({
    startTime: startTime.toUTC(),
    endTime: endTime.toUTC(),
    candidateID: candidate,
    osceID: osceTuple.osceID,
    tester: testerID,
  });
  return examSlot;
}

export function calculateOsceTuplesAndNumRoomsFromOsces(oscesUnsorted: OSCE[], examDay: ExamDay) {
  // sort osce's so at first all voice modules and then all text modules
  // TODO maybe do this as a function on the server/db -> have a look at Array.sort
  const osces: OSCE[] = [];
  const osceTuples: OsceTesterTuple[] = [];
  let numVoice = 0;
  oscesUnsorted
    .filter(osce => osce.taskType === 'voice')
    .forEach(osce => {
      osces.push(osce);
      const tuple = examDay.osceTesterTuples.find(tuple => tuple.osceID === osce._id);
      osceTuples.push(tuple!);
      numVoice += 1;
    });
  oscesUnsorted
    .filter(osce => osce.taskType === 'text')
    .forEach(osce => {
      osces.push(osce);
      const tuple = examDay.osceTesterTuples.find(tuple => tuple.osceID === osce._id);
      osceTuples.push(tuple!);
    });

  // create rooms depending on voice osces but fall back to 4 rooms in case no voice osce is added to this examday
  const numRooms = numVoice || 4;

  // Make sure osces and osceTuples are correctly sorted
  if (osceTuples.length !== osces.length)
    throw new Error("scheduleExam(): osces and osceTuples don't have same length");
  osceTuples.forEach((tuple, index) => {
    if (tuple.osceID !== osces[index]!._id)
      throw new Error('scheduleExam(): osces and osceTuples are no sorted identically!');
  });

  return { osces, osceTuples, numRooms, numVoice };
}

/**
 * - creates slots for given examday config, osces and osceTuples (assignment osce -> tester and oscegroup)
 * - iterates over candidates and osces
 * - rotates the osce array after each candidate iteration to ensure (compared with additional checks) no osce will start in parallel each round, keeping osce groups together while rotating.
 */
export function calculateRooms(
  examDay: Pick<
    EntityData<ExamDay>,
    'start' | 'slotPause' | 'candidateIDs' | 'breakAfterRounds' | 'breakInMinutes' | 'blockPause'
  >,
  numRooms: number,
  numVoice: number,
  osces: OSCE[],
  osceTuples: OsceTesterTuple[],
) {
  // initialize variables
  const dayStart = DateTime.isDateTime(examDay.start) ? examDay.start : DateTime.fromJSDate(examDay.start);
  const slotPause = examDay.slotPause;

  const rooms: Slot[][] = Array(numRooms)
    .fill(0)
    .map(() => []);
  let pastBreak = false; // track if we put a block after the break, gets ignored when there is no break
  let blockStartTime = dayStart;
  let tmpTime = dayStart;
  let round = 1; // start with first round

  if (
    osceTuples.find(
      osceTuple => osces.find(osce => osce._id === osceTuple.osceID)?.taskType === 'text' && osceTuple.osceGroupID,
    )
  )
    throw new Error('Scheduler cannot support Text Osces in OSCE groups at the moment');

  const generateGroupedOsces = (osces: OSCE[]) =>
    osces.reduce<OSCE[][]>((acc, curr) => {
      const getGroupForOsce = (osce: OSCE) => osceTuples.find(osceTuple => osceTuple.osceID === osce._id)?.osceGroupID;
      const currGroup = getGroupForOsce(curr);
      if (!currGroup) acc.push([curr]);
      else {
        const existingGroupIndex = acc.findIndex(osces => getGroupForOsce(osces[0]!) === currGroup);
        if (existingGroupIndex < 0) acc.push([curr]);
        else acc[existingGroupIndex]!.push(curr);
      }

      return acc;
    }, []);

  const sortOsces = (osces: OSCE[], rotateVoiceOsces?: boolean) => {
    const voiceOsces = osces.filter(osce => osce.taskType === 'voice');
    const textOsces = osces.filter(osce => osce.taskType === 'text');

    const groupedOsces = generateGroupedOsces(voiceOsces);

    // logger.debug(
    //   {
    //     voiceOsces: voiceOsces.map(osce => osce._id),
    //     rotateVoiceOsces,
    //     groupedOsces: groupedOsces.map(osces => osces.map(osce => osce._id)),
    //   },
    //   'sort osces',
    // );
    if (groupedOsces.length && rotateVoiceOsces) {
      const osceToMove = groupedOsces.splice(0, 1)[0]!;
      groupedOsces.splice(voiceOsces.length, 0, osceToMove);
    }

    return [...groupedOsces.flatMap(x => x), ...textOsces];
  };

  const osceIsEndOfGroup = (groups: OSCE[][], osce: OSCE) => {
    const groupIndex = groups.findIndex(group => group.map(osce => osce._id).indexOf(osce._id));
    if (groupIndex < 0) return true;
    const indexInsideGroup = groups[groupIndex]!.map(osce => osce._id).indexOf(osce._id);
    return indexInsideGroup === groups[groupIndex]!.length - 1;
  };

  osces = sortOsces(osces);

  // SCHEDULING

  for (let candidateIdx = 0; candidateIdx < examDay.candidateIDs.length; candidateIdx++) {
    // check if new block was started
    if (candidateIdx !== 0 && candidateIdx % numRooms === 0) {
      // START OF NEW BLOCK
      round = Math.ceil(candidateIdx / (numRooms || 1)) + 1;

      // set new blockStartTime
      if (examDay.breakAfterRounds && !pastBreak && round > examDay.breakAfterRounds) {
        blockStartTime = tmpTime.minus({ minutes: examDay.slotPause }).plus({ minutes: examDay.breakInMinutes });
        pastBreak = true;
      } else {
        blockStartTime = tmpTime.minus({ minutes: examDay.slotPause }).plus({ minutes: examDay.blockPause });
      }
    }

    let startTime: DateTime;
    for (let osceIdx = 0; osceIdx < osces.length; osceIdx++) {
      const alreadyPlannedSlotsThisRound = rooms.map(room => room.filter(slot => slot.startTime >= blockStartTime));
      const currentRoundRooms = alreadyPlannedSlotsThisRound.map(room => room[osceIdx]).filter(Boolean);
      // logger.info(
      //   {
      //     one: osces.map(osce => osce._id),
      //     two: alreadyPlannedSlotsThisRound.map(room => room.map(slot => slot.osceID)),
      //     three: currentRoundRooms.map(slot => slot?.osceID),
      //     four: alreadyPlannedSlotsThisRound[candidateIdx % numRooms]
      //       ?.filter(
      //         slot =>
      //           !osces
      //             .filter(osce => osce.taskType === 'text')
      //             .map(osce => osce._id)
      //             .includes(slot.osceID),
      //       )
      //       .map(slot => slot?.osceID),
      //   },
      //   'already planned slots this round: ',
      // );

      const idx = osces.findIndex(
        osce =>
          !currentRoundRooms
            .filter(
              slot =>
                !osces
                  .filter(osce => osce.taskType === 'text')
                  .map(osce => osce._id)
                  .includes(slot.osceID),
            )
            .map(slot => slot?.osceID)
            .includes(osce._id) &&
          !alreadyPlannedSlotsThisRound[candidateIdx % numRooms]
            ?.filter(
              slot =>
                !osces
                  .filter(osce => osce.taskType === 'text')
                  .map(osce => osce._id)
                  .includes(slot.osceID),
            )
            .map(slot => slot?.osceID)
            .includes(osce._id),
      );

      const osce = osces[idx]!;
      // logger.debug({ idx }, 'osce: ');

      // set new times
      if (osceIdx === 0) startTime = blockStartTime;
      else startTime = tmpTime;
      const endTime = startTime.plus({ minute: osce.durationInMinutes });

      // update tmpTime
      // TODO this is irrelevant if there is only one text task or when there is a break between them
      if (osce.taskType === 'text' && osces[(osceIdx + 1) % osces.length]!.taskType === 'text') tmpTime = endTime;
      else tmpTime = endTime.plus({ minute: slotPause });

      // add slots
      const osceTuple = osceTuples.find(osceTuple => osceTuple.osceID === osce._id);
      if (osceTuple) {
        const slot = createSlot(startTime, endTime, examDay.candidateIDs[candidateIdx]!, osceTuple, osce, round);

        rooms[candidateIdx % numRooms]!.push(slot);
      }
    }
    osces = sortOsces(osces, true);
  }

  return rooms;
}

export const scheduleExam = registerMutation({
  operationName: 'scheduleExam',
  type: () => Boolean,
  resolve: (examDayID: string) => {
    return async () => {
      const user = context.serverContext
        ? null
        : await CurrentUserInfo.getMyself<ILSUser>()({ _id: 1, roles: { name: 1, contexts: 1 } });
      if (!context.serverContext && !hasPermission(user, 'examdays', 'schedule'))
        throw new Error('No permission to schedule exam');

      // TODO calc possible candidates again to double check (possible to check with validators)

      // load examDay
      const examDay = await getByID(
        ExamDay,
        examDayID,
      )({
        ...ExamDay.roundCalculationSelector,
        slotIDs: 1,
        osceTesterTuples: { ...ExamDay.roundCalculationSelector.osceTesterTuples, osceID: 1, testerIDs: 1 },
        start: 1,
        slotPause: 1,
        candidateIDs: 1,
        breakAfterRounds: 1,
        breakInMinutes: 1,
        blockPause: 1,
        end: 1,
        fallbackTesters: 1,
      });
      if (!examDay) throw new Error(`scheduleExam(): Couldn't find examDay with id ${examDayID}`);
      const hasAnswers = await checkForAnswersInSlots(examDay.slotIDs)();
      if (hasAnswers) throw new Error('Der ExamDay hat bereits Antworten, es ist nicht mehr possível neu zu planen.');

      if (!examDay.candidateIDs.length)
        throw new Error('Die Prüfung hat keine Kandidaten. Sie kann deswegen nicht geplant werden.');
      const allTestersAssigned = examDay.osceTesterTuples.every(tuple =>
        tuple.testerIDs.every(t => t === null || t !== ''),
      );
      if (!allTestersAssigned)
        throw new Error('Die Prüfung hat nicht alle Schauspieler zugewiesen. Sie kann deswegen nicht geplant werden.');

      examDay.slotIDs = [];

      const oscesUnsorted = await OSCE.getByID<OSCE>(examDay.osceTesterTuples.map(tuple => tuple.osceID))({
        taskType: 1,
        durationInMinutes: 1,
      });

      const { osces, osceTuples, numRooms, numVoice } = calculateOsceTuplesAndNumRoomsFromOsces(oscesUnsorted, examDay);
      const rooms = calculateRooms(examDay, numRooms, numVoice, osces, osceTuples);

      examDay.slotIDs = (await Promise.all(rooms.flatMap(room => room.map(slot => slot.create())))).filter(Boolean);

      await examDay.save();
      logger.info({ numSlots: examDay.slotIDs.length }, 'Finished Scheduling. #Generated slots');

      return true;
    };
  },
  params: [{ type: () => ID, name: 'examDayID' }],
});
